import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { formatNumber } from '@debbie/common/dist/index.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { PaymentPlanStatus, getAmount, PaymentPlanEntryStatus, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { paymentPlans } from '@debbie/cortex/dist/api/index.js';
import { paymentPlans as paymentPlansApi } from '@debbie/cortex/dist/api/index.js';
import * as paymentPlansLib from '@debbie/cortex/dist/api/payment-plans.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { File, ButtonGroup, Well } from '../index.js';
import THEME from '../../theme.js';
import Button from '../Button.js';
import { Row, Col } from '../Grid/index.js';
import ArrowRight from '../Icons/ArrowRight.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import Logs from './PaymentPlanLogs.js';
import q from '@debbie/cortex/dist/query.js';
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import ExclusiveContent, { ExclusiveContentBadge } from '../ExclusiveContent.js';
const PaymentPlanData = (props) => {
    const lang = useSelector((s) => s.lang);
    const tenant = useSelector(selectTenant);
    const documentsLoader = useDocumentsByReference(props.singleCase.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const creditorLoadable = useCreditor(props.singleCase.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const [changingStatus, setChangingStatus] = useState(false);
    const [deletingPaymentPLan, setDeletingPaymentPLan] = useState(false);
    const { caseVouchers, singleCase, paymentPlan } = props;
    const document = documents?.find((document) => document.documentId === paymentPlan.documentId);
    const file = document && (document.signedFile || document.file);
    const upcomingEntries = paymentPlan.plan.filter((entry, i) => ![PaymentPlanEntryStatus.SETTLED].includes(entry.status) &&
        moment(entry.reminder).isAfter(moment()) &&
        i !==
            paymentPlan.plan.findIndex((e) => ![PaymentPlanEntryStatus.SETTLED].includes(e.status)));
    const settledEntries = paymentPlan.plan.filter((entry) => [PaymentPlanEntryStatus.SETTLED].includes(entry.status));
    const skipTo = upcomingEntries[0]?.date ||
        moment().add(1, 'month').startOf('month').format();
    const skipNextEntry = async () => {
        if (!paymentPlan.config)
            throw 'Config missing';
        const config = {
            amount: paymentPlan.config.amount,
            duration: paymentPlan.config.duration
                ? paymentPlan.config.duration - settledEntries.length
                : undefined,
            dateUpfront: null,
            amountUpfront: null,
            interestFree: paymentPlan.config.interestFree,
            interval: paymentPlan.config.interval,
            startDate: skipTo,
        };
        if (tenant == null || creditor == null || documents == null) {
            throw new Error('Tenant or creditor data is still loading');
        }
        const plan = helpers.paymentPlans.gen(tenant, creditor, JSON.parse(JSON.stringify(helpers.vouchers.postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        }))), documents, config);
        await paymentPlansApi.create(paymentPlan.caseId, {
            activationTrigger: false,
            plan: plan.map((entry) => ({
                amount: entry.amount,
                cancel: entry.cancel,
                deadline: entry.deadline,
                reminder: entry.reminder,
                date: entry.date,
                type: entry.type,
            })),
            config: config
                ? {
                    interval: config.interval,
                    startDate: config.startDate,
                    duration: config.duration,
                    amount: config.amount,
                    amountUpfront: config.amountUpfront,
                    dateUpfront: config.dateUpfront,
                    interestFree: config.interestFree,
                }
                : null,
        });
        q.invalidate(keys.write.create(paymentPlansEntity));
    };
    return (React.createElement(Well, { style: { margin: '20px 0' } },
        React.createElement(Row, null,
            React.createElement(Col, { width: "6" },
                paymentPlan.plan && (React.createElement("div", null,
                    React.createElement("h4", null, lang.paymentPlans.paymentPlan),
                    React.createElement("div", null,
                        React.createElement(Logs, { paymentPlanId: props.paymentPlan.paymentPlanId, paymentPlanEntryId: null })),
                    React.createElement("p", null,
                        moment(paymentPlan.plan[0] && paymentPlan.plan[0].date).format(THEME.DATE_FORMAT_SHORT),
                        ' ',
                        React.createElement(ArrowRight, null),
                        ' ',
                        moment(paymentPlan.plan[paymentPlan.plan.length - 1] &&
                            paymentPlan.plan[paymentPlan.plan.length - 1].date).format(THEME.DATE_FORMAT_SHORT)),
                    React.createElement("p", null,
                        paymentPlan.plan.length,
                        " ",
                        lang.paymentPlans.entries),
                    React.createElement("p", null,
                        formatNumber(getAmount.entry(paymentPlan.plan) || 0),
                        ' ',
                        singleCase && singleCase.currency,
                        "/",
                        lang.paymentPlans.month),
                    getAmount.upfront(paymentPlan.plan) && (React.createElement("p", null,
                        lang.paymentPlans.upfrontPayment,
                        ":",
                        ' ',
                        formatNumber(getAmount.upfront(paymentPlan.plan) || 0))),
                    getAmount.rest(paymentPlan.plan) && (React.createElement("p", null,
                        lang.paymentPlans.rest,
                        ":",
                        ' ',
                        formatNumber(getAmount.rest(paymentPlan.plan) || 0))))),
                file && (React.createElement("div", null,
                    React.createElement("h4", null, lang.paymentPlans.agreement),
                    React.createElement("p", null,
                        React.createElement(File, { file: file }))))),
            React.createElement(Col, { width: "6" },
                React.createElement(ButtonGroup, null,
                    [PaymentPlanStatus.CREATED, PaymentPlanStatus.ACTIVE].includes(paymentPlan.status) && (React.createElement(Button, { onClick: async () => {
                            setDeletingPaymentPLan(true);
                            await paymentPlansLib.del(paymentPlan.paymentPlanId);
                            q.invalidate(keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId));
                            setDeletingPaymentPLan(false);
                        }, size: "small", theme: "dangerous", loading: deletingPaymentPLan }, lang.paymentPlans.deletePaymentPlan)),
                    paymentPlan.status === PaymentPlanStatus.CREATED && (React.createElement(Button, { style: { float: 'right' }, size: "small", theme: "light", loading: changingStatus, onClick: async () => {
                            setChangingStatus(true);
                            await paymentPlans.patch(paymentPlan.paymentPlanId, {
                                status: PaymentPlanStatus.ACTIVE,
                            });
                            q.invalidate(keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId));
                            setChangingStatus(false);
                        }, "data-tooltip-id": "tooltip-default", "data-tooltip-content": lang.paymentPlans.activatePaymentPlanWithoutFoundation }, lang.paymentPlans.activate))),
                React.createElement("br", null),
                React.createElement("br", null),
                React.createElement(ButtonGroup, null, [PaymentPlanStatus.ACTIVE, PaymentPlanStatus.CANCELED].includes(paymentPlan.status) &&
                    paymentPlan.config && (React.createElement(Button, { size: "small", onClick: skipNextEntry },
                    lang.paymentPlans.skipTo,
                    " ",
                    moment(skipTo).format('DD/MM')))),
                [PaymentPlanStatus.CREATED, PaymentPlanStatus.ACTIVE].includes(paymentPlan.status) && (React.createElement(ExclusiveContent, null,
                    React.createElement("br", null),
                    React.createElement("br", null),
                    React.createElement("div", { style: {
                            padding: 4,
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                        } },
                        React.createElement(ExclusiveContentBadge, null),
                        React.createElement("div", null, "Genberegning sletter alle entries som afventer og udregner planen igen."),
                        React.createElement(Button, { onClick: async () => {
                                await paymentPlans.recalculate(paymentPlan.paymentPlanId);
                                q.invalidate(keys.write.modify(paymentPlansEntity, paymentPlan.paymentPlanId));
                            }, size: "small", theme: "light" }, "Genberegn"))))))));
};
export default PaymentPlanData;
//# sourceMappingURL=PaymentPlanData.js.map